import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { HIRE_PAGE_CONTAINER, HIRE_SECTION_SPACING } from '@/lib/hireResponsive';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Search, UserSearch, MapPin, Briefcase, GraduationCap,
  Clock, Globe, Send, X, EyeOff, Loader2, SlidersHorizontal,
  LayoutGrid, List, Building2, Eye, Languages, Award, CheckCircle2, Lock,
  AlertTriangle, Mail, Phone,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTalentSearch, type TalentCandidate } from '@/hooks/useTalentSearch';
import { usePermissions } from '@/hooks/usePermissions';
import { TalentDiscoveryMeter } from '@/components/talent-discovery/TalentDiscoveryMeter';
import { TalentInviteSendButton } from '@/components/talent-discovery/TalentInviteSendButton';
import { useJobs } from '@/hooks/useJobs';
import { useServiceAccess } from '@/hooks/useServiceAccess';
import { useEntitlement } from '@/hooks/useEntitlement';
import { useServiceGovernance } from '@/hooks/useServiceGovernance';
import { SERVICE_KEYS } from '@/lib/serviceKeys';

import { useTranslation } from 'react-i18next';

function TalentDiscovery() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { hasAccess: hasTalentPool } = useServiceAccess(SERVICE_KEYS.TALENT_POOL);
  const talentEntitlement = useEntitlement(SERVICE_KEYS.TALENT_POOL);
  const talentGov = useServiceGovernance('talent_pool');
  const inviteBlocked = !talentGov.allowed;
  const {
    filters, setFilters, candidates, total,
    isLoading, isFetching, search, resetFilters,
    sendInvite, isSendingInvite,
  } = useTalentSearch();
  const { data: jobs } = useJobs();

  const [keywordInput, setKeywordInput] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const [langInput, setLangInput] = useState('');
  const [inviteDialog, setInviteDialog] = useState<TalentCandidate | null>(null);
  const [inviteJobId, setInviteJobId] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'rows'>('cards');
  const [detailCandidate, setDetailCandidate] = useState<TalentCandidate | null>(null);

  // Debounced keyword search
  const keywordTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    clearTimeout(keywordTimer.current);
    keywordTimer.current = setTimeout(() => {
      if (filters.keyword !== keywordInput) {
        search({ keyword: keywordInput, page: 1 });
      }
    }, 400);
    return () => clearTimeout(keywordTimer.current);
  }, [keywordInput]);

  const activeJobs = (jobs || []).filter(j => j.status === 'active');
  const activeJobIdSet = new Set(activeJobs.map(j => j.id));
  const getActiveInviteCount = (candidate?: TalentCandidate | null) => {
    if (!candidate?.invite_job_statuses) return 0;
    return Object.keys(candidate.invite_job_statuses).filter((jobId) => activeJobIdSet.has(jobId)).length;
  };

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !filters.skills.includes(s)) {
      setFilters(prev => ({ ...prev, skills: [...prev.skills, s] }));
    }
    setSkillInput('');
  };

  const removeSkill = (s: string) => {
    setFilters(prev => ({ ...prev, skills: prev.skills.filter(x => x !== s) }));
  };

  const addLang = () => {
    const l = langInput.trim();
    if (l && !filters.languages.includes(l)) {
      setFilters(prev => ({ ...prev, languages: [...prev.languages, l] }));
    }
    setLangInput('');
  };

  const removeLang = (l: string) => {
    setFilters(prev => ({ ...prev, languages: prev.languages.filter(x => x !== l) }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    search({ page: 1 });
  };

  // Re-invite confirmation state
  // Re-invite confirmation state
  const [reinviteWarning, setReinviteWarning] = useState<string | null>(null);
  const [pendingInviteParams, setPendingInviteParams] = useState<{ candidate_id: string; target_hire_job_id?: string; invite_message?: string } | null>(null);

  const handleInvite = () => {
    if (!inviteDialog) return;
    const params = {
      candidate_id: inviteDialog.candidate_id,
      target_hire_job_id: inviteJobId || undefined,
      invite_message: inviteMessage || undefined,
    };

    // Check if this is a re-invite for a withdrawn/declined job
    const jobStatus = inviteDialog.invite_job_statuses?.[inviteJobId];
    if (jobStatus && (jobStatus.status === 'withdrawn' || jobStatus.status === 'declined')) {
      if (jobStatus.attempts >= 2) {
        // Should already be blocked in the select, but double-check
        return;
      }
      const statusLabel = jobStatus.status === 'withdrawn'
        ? t('hire.talent.inviteStatuses.withdrawn')
        : t('hire.talent.inviteStatuses.declined');
      const remaining = 2 - jobStatus.attempts;
      setReinviteWarning(t('hire.talent.reinviteWarning', { status: statusLabel, count: remaining }));
      setPendingInviteParams(params);
      setInviteDialog(null);
      return;
    }

    sendInvite(params);
    setInviteDialog(null);
    setInviteJobId('');
    setInviteMessage('');
  };

  const confirmReinvite = () => {
    if (pendingInviteParams) {
      sendInvite(pendingInviteParams);
    }
    setReinviteWarning(null);
    setPendingInviteParams(null);
    setInviteJobId('');
    setInviteMessage('');
  };

  if (!can('talent_pool', 'search') || !hasTalentPool) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
          {!hasTalentPool ? (
            <>
              <Lock className="w-10 h-10 text-muted-foreground" />
              <p className="text-muted-foreground font-medium">{t('hire.talent.notIncluded')}</p>
              <p className="text-sm text-muted-foreground">{t('hire.talent.contactAdminToUpgrade')}</p>
              <Button variant="outline" onClick={() => navigate('/settings/billing')} className="mt-2">
                {t('hire.talent.upgradePlan')}
              </Button>
            </>
          ) : (
            <p className="text-muted-foreground">{t('hire.talent.noPermission')}</p>
          )}
        </div>
      </AppLayout>
    );
  }

  // Entitlement-based usage warning (allowed but near limit or using credits)
  const talentNearLimit = !talentEntitlement.isLoading && talentEntitlement.isNearLimit;
  const talentUsingCredits = !talentEntitlement.isLoading && talentEntitlement.isUsingCredits;

  const filterContent = (
    <form onSubmit={handleSearch} className="space-y-5">
      <div className="space-y-1.5">
        <Label className="text-sm font-semibold text-foreground">{t('hire.talent.jobTitleRole')}</Label>
        <Input
          placeholder={t('hire.talent.titlePlaceholder')}
          value={filters.title}
          onChange={e => setFilters(prev => ({ ...prev, title: e.target.value }))}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm font-semibold text-foreground">{t('hire.talent.company')}</Label>
        <div className="flex gap-2">
          <Input
            className="flex-1"
            placeholder={t('hire.talent.companyPlaceholder')}
            value={filters.company}
            onChange={e => setFilters(prev => ({ ...prev, company: e.target.value }))}
          />
          <Select
            value={filters.company_tenure || 'all'}
            onValueChange={v => setFilters(prev => ({ ...prev, company_tenure: v === 'all' ? '' : v }))}
          >
            <SelectTrigger className="w-[120px] shrink-0"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="current">{t('hire.talent.companyTenure.current')}</SelectItem>
              <SelectItem value="previous">{t('hire.talent.companyTenure.previous')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm font-semibold text-foreground">{t('hire.talent.location')}</Label>
        <Input
          placeholder={t('hire.talent.locationPlaceholder')}
          value={filters.location}
          onChange={e => setFilters(prev => ({ ...prev, location: e.target.value }))}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-sm font-semibold text-foreground">{t('hire.talent.minExp')}</Label>
          <Input
            type="number"
            min={0}
            placeholder="0"
            value={filters.min_experience ?? ''}
            onChange={e => setFilters(prev => ({
              ...prev,
              min_experience: e.target.value ? Number(e.target.value) : undefined,
            }))}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-semibold text-foreground">{t('hire.talent.maxExp')}</Label>
          <Input
            type="number"
            min={0}
            placeholder="20"
            value={filters.max_experience ?? ''}
            onChange={e => setFilters(prev => ({
              ...prev,
              max_experience: e.target.value ? Number(e.target.value) : undefined,
            }))}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm font-semibold text-foreground">{t('hire.talent.educationLevel')}</Label>
        <Select
          value={filters.education_level}
          onValueChange={v => setFilters(prev => ({ ...prev, education_level: v === 'any' ? '' : v }))}
        >
          <SelectTrigger><SelectValue placeholder={t('common.any')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{t('common.any')}</SelectItem>
            <SelectItem value="High School">{t('hire.talent.education.highSchool')}</SelectItem>
            <SelectItem value="Diploma">{t('hire.talent.education.diploma')}</SelectItem>
            <SelectItem value="Bachelor's">{t('hire.talent.education.bachelors')}</SelectItem>
            <SelectItem value="Master's">{t('hire.talent.education.masters')}</SelectItem>
            <SelectItem value="PhD / Doctorate">{t('hire.talent.education.doctorate')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm font-semibold text-foreground">{t('hire.talent.availability')}</Label>
        <Select
          value={filters.availability}
          onValueChange={v => setFilters(prev => ({ ...prev, availability: v === 'any' ? '' : v }))}
        >
          <SelectTrigger><SelectValue placeholder={t('common.any')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            <SelectItem value="open">{t('hire.talent.openToOffers')}</SelectItem>
            <SelectItem value="actively_looking">{t('hire.talent.activelyLooking')}</SelectItem>
            <SelectItem value="not_looking">{t('hire.talent.notLooking')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm font-semibold text-foreground">{t('hire.talent.skills')}</Label>
        <div className="flex gap-1.5">
          <Input
            placeholder={t('hire.talent.addSkill')}
            value={skillInput}
            onChange={e => setSkillInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
            className="flex-1"
          />
          <Button type="button" size="icon" variant="outline" onClick={addSkill} className="h-10 w-10 flex-shrink-0">
            <span className="text-lg">+</span>
          </Button>
        </div>
        {filters.skills.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {filters.skills.map(s => (
              <Badge key={s} variant="secondary" className="text-xs gap-1 rounded-lg">
                {s}
                <X className="w-3 h-3 cursor-pointer" onClick={() => removeSkill(s)} />
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm font-semibold text-foreground">{t('hire.talent.languages')}</Label>
        <div className="flex gap-1.5">
          <Input
            placeholder={t('hire.talent.addLanguage')}
            value={langInput}
            onChange={e => setLangInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLang(); } }}
            className="flex-1"
          />
          <Button type="button" size="icon" variant="outline" onClick={addLang} className="h-10 w-10 flex-shrink-0">
            <span className="text-lg">+</span>
          </Button>
        </div>
        {filters.languages.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {filters.languages.map(l => (
              <Badge key={l} variant="secondary" className="text-xs gap-1 rounded-lg">
                {l}
                <X className="w-3 h-3 cursor-pointer" onClick={() => removeLang(l)} />
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" className="flex-1 rounded-full press-scale" disabled={isLoading || isFetching}>
          {(isLoading || isFetching) ? (
            <Loader2 className="w-4 h-4 animate-spin me-2" />
          ) : (
            <Search className="w-4 h-4 me-2" />
          )}
           {t('common.search')}
        </Button>
         <Button type="button" variant="ghost" onClick={resetFilters} className="font-semibold">
           {t('common.clear')}
         </Button>
      </div>
    </form>
  );

  return (
    <AppLayout>
      <div className={`${HIRE_SECTION_SPACING} ${HIRE_PAGE_CONTAINER}`}>
        {/* Header */}
        <Card className="shadow-card border-border border-t-4 border-t-primary overflow-hidden">
          <div className="p-5 sm:p-6 flex items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-display text-foreground">{t('hire.talent.title')}</h1>
              <p className="text-muted-foreground mt-1.5 text-sm">
                {t('hire.talent.subtitle')}
              </p>
            </div>
          <div className="flex items-center gap-2">
            {/* Mobile filter trigger */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="lg:hidden rounded-xl h-9">
                  <SlidersHorizontal className="w-4 h-4 me-1.5" />
                   {t('common.filters')}
                </Button>
              </SheetTrigger>
              <SheetContent side={isRtl ? 'right' : 'left'} className="w-80">
                <SheetHeader>
                  <SheetTitle className="font-display">{t('hire.talent.searchFilters')}</SheetTitle>
                </SheetHeader>
                <div className="mt-6">{filterContent}</div>
              </SheetContent>
            </Sheet>
          </div>
          </div>
        </Card>

        <div className="flex gap-6">
          {/* Desktop Sticky Sidebar */}
          <div className="hidden lg:block w-72 xl:w-80 flex-shrink-0">
            <div className="sticky top-20">
              <Card className="shadow-card border-border rounded-xl">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 font-display">
                     <UserSearch className="w-4 h-4 text-primary" />
                     {t('hire.talent.searchFilters')}
                  </CardTitle>
                </CardHeader>
                <CardContent>{filterContent}</CardContent>
              </Card>
            </div>
          </div>

          {/* Results column */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Entitlement warnings */}
            {talentUsingCredits && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-300/50 bg-amber-50 dark:bg-amber-950/20 px-4 py-2.5 text-sm">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span className="text-amber-800 dark:text-amber-300">{t('hire.talent.usingCreditsWarning', { count: talentEntitlement.creditBalance })}</span>
              </div>
            )}
            {talentNearLimit && !talentUsingCredits && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-300/50 bg-amber-50 dark:bg-amber-950/20 px-4 py-2.5 text-sm">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span className="text-amber-800 dark:text-amber-300">{t('hire.talent.nearLimitWarning', { percent: talentEntitlement.usagePercent, used: talentEntitlement.used, limit: talentEntitlement.limit })}</span>
              </div>
            )}
            {/* Top search bar + view toggle */}
            <div className="bg-white rounded-xl border border-border p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t('hire.talent.searchPlaceholder')}
                    className="ps-10 h-11 rounded-xl bg-background"
                    value={keywordInput}
                    onChange={e => setKeywordInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        clearTimeout(keywordTimer.current);
                        search({ keyword: keywordInput, page: 1 });
                      }
                    }}
                  />
                </div>
                <div className="flex items-center border border-border rounded-xl overflow-hidden flex-shrink-0">
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`p-2.5 transition-colors ${viewMode === 'cards' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'}`}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('rows')}
                    className={`p-2.5 transition-colors ${viewMode === 'rows' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {total > 0 && (
                 <p className="text-sm text-muted-foreground">
                   {t('hire.talent.foundCandidates', { count: total })}
                 </p>
              )}
            </div>

            {candidates.length === 0 && !isLoading && !isFetching && (
              <Card className="rounded-xl">
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                   <UserSearch className="w-14 h-14 text-muted-foreground/30 mb-4" />
                   <h3 className="font-display font-semibold text-foreground mb-1">{t('hire.talent.searchTalentPool')}</h3>
                   <p className="text-sm text-muted-foreground max-w-md">
                     {t('hire.talent.searchTalentPoolDesc')}
                   </p>
                </CardContent>
              </Card>
            )}

            {/* Card Grid View */}
            {viewMode === 'cards' && candidates.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {candidates.map(c => (
                  <CandidateGridCard
                    key={c.candidate_id}
                    candidate={c}
                    inviteCount={getActiveInviteCount(c)}
                    onInvite={() => setInviteDialog(c)}
                    onViewDetail={() => setDetailCandidate(c)}
                    canInvite={can('talent_pool', 'invite') && !inviteBlocked}
                  />
                ))}
              </div>
            )}

            {/* Row List View */}
            {viewMode === 'rows' && candidates.length > 0 && (
              <div className="space-y-3">
                {candidates.map(c => (
                  <CandidateRowCard
                    key={c.candidate_id}
                    candidate={c}
                    inviteCount={getActiveInviteCount(c)}
                    onInvite={() => setInviteDialog(c)}
                    onViewDetail={() => setDetailCandidate(c)}
                    canInvite={can('talent_pool', 'invite') && !inviteBlocked}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invite Dialog */}
      <Dialog open={!!inviteDialog} onOpenChange={() => setInviteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">{t('hire.talent.inviteToApply', { name: inviteDialog?.visibility_mode === 'anonymous' ? t('hire.talent.anonymousCandidate') : inviteDialog?.display_name })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>{t('hire.talent.selectJob')} <span className="text-destructive">*</span></Label>
              <Select value={inviteJobId} onValueChange={setInviteJobId}>
                <SelectTrigger><SelectValue placeholder={t('hire.talent.chooseJob')} /></SelectTrigger>
                <SelectContent>
                  {activeJobs.map(j => {
                    const jobStatus = inviteDialog?.invite_job_statuses?.[j.id];
                    const status = jobStatus?.status;
                    const isBlocked = jobStatus && jobStatus.attempts >= 2 && (status === 'withdrawn' || status === 'declined');
                    const isPending = status === 'pending';
                    const isReinvited = status === 're-invited';
                    const isAccepted = status === 'accepted';
                    const isWithdrawn = status === 'withdrawn';
                    const isDeclined = status === 'declined';
                    const disabled = isPending || isReinvited || isAccepted || isDeclined || !!isBlocked;

                    let label = j.title;
                    if (isBlocked) label += ` ${t('hire.talent.jobInviteLabels.cooldownBlocked')}`;
                    else if (isPending) label += ` ${t('hire.talent.jobInviteLabels.invitePending')}`;
                    else if (isReinvited) label += ` ${t('hire.talent.jobInviteLabels.reinvitedPending')}`;
                    else if (isAccepted) label += ` ${t('hire.talent.jobInviteLabels.accepted')}`;
                    else if (isWithdrawn) label += ` ${t('hire.talent.jobInviteLabels.withdrawnReinvite')}`;
                    else if (isDeclined) label += ` ${t('hire.talent.jobInviteLabels.declined')}`;

                    return (
                      <SelectItem key={j.id} value={j.id} disabled={disabled}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {inviteDialog && getActiveInviteCount(inviteDialog) > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t('hire.talent.alreadyInvitedActiveJobs', { count: getActiveInviteCount(inviteDialog) })}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
               <Label>{t('hire.talent.message')}</Label>
              <Textarea
                 placeholder={t('hire.talent.messagePlaceholder')}
                value={inviteMessage}
                onChange={e => setInviteMessage(e.target.value)}
                rows={3}
              />
            </div>
            <TalentDiscoveryMeter variant="compact" />
          </div>
          <DialogFooter>
             <Button variant="ghost" onClick={() => setInviteDialog(null)}>{t('common.cancel')}</Button>
             <TalentInviteSendButton
               onClick={handleInvite}
               isSending={isSendingInvite}
               jobSelected={!!inviteJobId}
               t={t}
             />
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Candidate Detail Dialog */}
      <Dialog open={!!detailCandidate} onOpenChange={() => setDetailCandidate(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{t('hire.talent.candidateProfile')}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {t('hire.talent.candidateProfileDescription')}
            </DialogDescription>
          </DialogHeader>
          {detailCandidate && <CandidateDetailContent candidate={detailCandidate} />}
          <DialogFooter>
          {can('talent_pool', 'invite') && !inviteBlocked && (
              detailCandidate?.allow_invites === false ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex">
                      <Button variant="outline" disabled className="opacity-60 pointer-events-none">
                        <Lock className="w-4 h-4 me-2" />
                        {t('hire.talent.invitationsClosed')}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="z-[9999]">
                    <p>{t('hire.talent.candidateNotAcceptingInvites')}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  onClick={() => {
                    const c = detailCandidate;
                    setDetailCandidate(null);
                    if (c) setInviteDialog(c);
                  }}
                  className="press-scale"
                >
                  <Send className="w-4 h-4 me-2" />
                  {t('hire.talent.invite')}
                  {getActiveInviteCount(detailCandidate) > 0 && (
                    <Badge variant="secondary" className="ms-2 text-[10px] px-1.5 py-0">
                      {t('hire.talent.sentCount', { count: getActiveInviteCount(detailCandidate) })}
                    </Badge>
                  )}
                </Button>
              )
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Re-invite Warning Dialog */}
      <Dialog open={!!reinviteWarning} onOpenChange={() => { setReinviteWarning(null); setPendingInviteParams(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              {t('hire.talent.confirmReinviteTitle')}
            </DialogTitle>
            <DialogDescription>{reinviteWarning}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setReinviteWarning(null); setPendingInviteParams(null); }}>{t('common.cancel')}</Button>
            <Button onClick={confirmReinvite} disabled={isSendingInvite} className="press-scale">
              <Send className="w-4 h-4 me-2" />
              {t('hire.talent.confirmReinvite')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

/* ─── Candidate Detail Content (popup) ─── */
function CandidateDetailContent({ candidate: c }: { candidate: TalentCandidate }) {
  const { t } = useTranslation();
  const isAnonymous = c.visibility_mode === 'anonymous';
  const displayName = isAnonymous ? t('hire.talent.anonymousCandidate') : c.display_name;
  const initials = isAnonymous
    ? '?'
    : c.display_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const phoneLabel = c.phone_visibility === 'visible' && c.phone
    ? c.phone
    : c.phone_visibility === 'not_available'
      ? t('hire.talent.notProvidedByCandidate')
      : t('hire.talent.hiddenByCandidate');

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Avatar className="h-14 w-14 rounded-xl">
          {!isAnonymous && c.avatar_url && <AvatarImage src={c.avatar_url} alt={displayName} />}
          <AvatarFallback className="rounded-xl bg-primary/10 text-primary font-bold text-lg uppercase">
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
           <div className="flex items-center gap-2 flex-wrap">
             <h3 className="font-display font-bold text-foreground text-lg">{displayName}</h3>
             {isAnonymous && (
               <Badge variant="secondary" className="text-[10px] px-1.5 py-0 rounded-lg">
                 <EyeOff className="w-3 h-3 me-0.5" /> {t('hire.talent.anonymous')}
               </Badge>
             )}
             {c.visibility_mode === 'open' && (
               <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 shadow-[0_0_8px_hsl(145_60%_45%/0.35)] animate-pulse">
                 <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                 {t('hire.talent.openToWork')}
               </span>
             )}
           </div>
          {c.current_job_title && (
            <p className="text-sm text-primary font-medium">{c.current_job_title}</p>
          )}
          {c.headline && !c.current_job_title && (
            <p className="text-sm text-primary font-medium">{c.headline}</p>
          )}
          {isAnonymous && (
            <p className="text-xs text-muted-foreground mt-1">{t('hire.talent.identityRevealHint')}</p>
          )}
        </div>
      </div>

      <Separator />

      <div>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('hire.talent.contact')}</h4>
        <div className="grid grid-cols-1 gap-2 text-sm">
          {c.email && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="w-4 h-4 text-primary" />
              <span>{c.email}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="w-4 h-4 text-primary" />
            <span>{phoneLabel}</span>
          </div>
        </div>
      </div>

      {/* Key Info */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        {c.country && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="w-4 h-4 text-primary" />
            <span>{c.city ? `${c.city}, ` : ''}{c.country}</span>
          </div>
        )}
        {c.total_years_experience != null && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Briefcase className="w-4 h-4 text-primary" />
            <span>{t('hire.talent.yearsExperience', { count: c.total_years_experience })}</span>
          </div>
        )}
        {c.highest_education_level && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <GraduationCap className="w-4 h-4 text-primary" />
            <span>{c.highest_education_level}</span>
          </div>
        )}
        {c.availability_status && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="w-4 h-4 text-primary" />
            <span>{c.availability_status === 'open' ? t('hire.talent.openToOffers') : c.availability_status === 'actively_looking' ? t('hire.talent.activelyLooking') : c.availability_status === 'not_looking' ? t('hire.talent.notLooking') : c.availability_status.replace(/_/g, ' ')}</span>
          </div>
        )}
      </div>

      {/* Summary */}
      {c.professional_summary && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{t('hire.talent.summary')}</h4>
          <p className="text-sm text-foreground leading-relaxed">{c.professional_summary}</p>
        </div>
      )}

      {/* Skills */}
      {c.skills.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('hire.talent.skills')}</h4>
          <div className="flex flex-wrap gap-1.5">
            {c.skills.map(s => (
              <span
                key={s.skill_name}
                className="text-xs px-2.5 py-1 rounded-lg border border-primary/20 bg-primary/5 text-primary"
              >
                {s.skill_name}
                {s.proficiency_level && (
                  <span className="text-muted-foreground ms-1">· {s.proficiency_level}</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Languages */}
      {c.languages.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('hire.talent.languages')}</h4>
          <div className="flex flex-wrap gap-1.5">
            {c.languages.map(l => (
              <Badge key={l.language_name} variant="outline" className="rounded-lg text-xs">
                <Languages className="w-3 h-3 me-1" />
                {l.language_name}
                {l.proficiency_level && <span className="text-muted-foreground ms-1">· {l.proficiency_level}</span>}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Certifications */}
      {c.certifications && c.certifications.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('hire.talent.certifications')}</h4>
          <div className="flex flex-wrap gap-1.5">
            {c.certifications.map(cert => (
              <span
                key={cert.certification_name}
                className="text-xs px-2.5 py-1 rounded-lg border border-primary/20 bg-primary/5 text-primary shadow-[0_0_6px_hsl(var(--primary)/0.15)]"
              >
                <Award className="w-3 h-3 inline me-1" />
                {cert.certification_name}
                {cert.issuing_organization && (
                  <span className="text-muted-foreground ms-1">· {cert.issuing_organization}</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {(c.preferred_work_modes.length > 0 || c.preferred_industries.length > 0) && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('hire.talent.preferences')}</h4>
          <div className="flex flex-wrap gap-1.5">
            {c.preferred_work_modes.map(m => (
              <Badge key={m} variant="secondary" className="rounded-lg text-xs capitalize">
                {m.replace(/_/g, ' ')}
              </Badge>
            ))}
            {c.preferred_industries.map(i => (
              <Badge key={i} variant="secondary" className="rounded-lg text-xs">
                {i}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Grid Card (compact highlight card) ─── */
function CandidateGridCard({
  candidate: c,
  inviteCount,
  onInvite,
  onViewDetail,
  canInvite,
}: {
  candidate: TalentCandidate;
  inviteCount: number;
  onInvite: () => void;
  onViewDetail: () => void;
  canInvite: boolean;
}) {
  const { t } = useTranslation();
  const isAnonymous = c.visibility_mode === 'anonymous';
  const displayName = isAnonymous ? t('hire.talent.anonymousCandidate') : c.display_name;
  const initials = isAnonymous
    ? '?'
    : c.display_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <Card
      className="hover:shadow-card-hover hover:border-primary/20 transition-all rounded-xl border-t-4 border-t-primary cursor-pointer"
      onClick={onViewDetail}
    >
      <CardContent className="p-5 space-y-3">
        {/* Avatar + Name */}
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 rounded-xl">
            {!isAnonymous && c.avatar_url && <AvatarImage src={c.avatar_url} alt={displayName} />}
            <AvatarFallback className="rounded-xl bg-primary/10 text-primary text-sm font-bold uppercase">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
             <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display font-bold text-foreground truncate text-sm">{displayName}</h3>
              {isAnonymous && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 rounded-lg">
                  <EyeOff className="w-3 h-3 me-0.5" /> {t('hire.talent.anonymous')}
                </Badge>
              )}
              {c.visibility_mode === 'open' && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 shadow-[0_0_8px_hsl(145_60%_45%/0.35)] animate-pulse">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {t('hire.talent.openToWork')}
                </span>
              )}
            </div>
            {c.current_job_title && (
              <p className="text-xs text-primary font-medium mt-0.5 line-clamp-1">{c.current_job_title}</p>
            )}
            {!c.current_job_title && c.headline && (
              <p className="text-xs text-primary font-medium mt-0.5 line-clamp-1">{c.headline}</p>
            )}
          </div>
        </div>

        {/* Location + Experience */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          {c.country && (
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              {c.city ? `${c.city}, ` : ''}{c.country}
            </span>
          )}
          {c.total_years_experience != null && (
            <span className="flex items-center gap-1.5">
              <Briefcase className="w-3.5 h-3.5" />
              {t('hire.talent.yearsShort', { count: c.total_years_experience })}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); onViewDetail(); }}
            className="text-xs h-8 px-2 text-muted-foreground hover:text-foreground"
          >
            <Eye className="w-3.5 h-3.5 me-1" />
            {t('hire.talent.viewProfile')}
          </Button>
          {canInvite && (
            c.allow_invites === false ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="inline-flex" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" disabled className="rounded-full text-xs h-8 px-3 opacity-60 pointer-events-none">
                      <Lock className="w-3 h-3 me-1" />
                      {t('hire.talent.closed')}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="z-[9999]"><p>{t('hire.talent.candidateNotAcceptingInvites')}</p></TooltipContent>
              </Tooltip>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); onInvite(); }}
                className="rounded-full press-scale text-xs h-8 px-3"
              >
                <Send className="w-3 h-3 me-1" />
                {t('hire.talent.invite')}
                {inviteCount > 0 && (
                  <Badge variant="secondary" className="ms-1 text-[9px] px-1 py-0">{inviteCount}</Badge>
                )}
              </Button>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Row Card (list view) ─── */
function CandidateRowCard({
  candidate: c,
  inviteCount,
  onInvite,
  onViewDetail,
  canInvite,
}: {
  candidate: TalentCandidate;
  inviteCount: number;
  onInvite: () => void;
  onViewDetail: () => void;
  canInvite: boolean;
}) {
  const { t } = useTranslation();
  const isAnonymous = c.visibility_mode === 'anonymous';
  const displayName = isAnonymous ? t('hire.talent.anonymousCandidate') : c.display_name;
  const initials = isAnonymous
    ? '?'
    : c.display_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <Card
      className="hover:shadow-card-hover hover:border-primary/20 transition-all rounded-xl border-s-4 border-s-primary cursor-pointer"
      onClick={onViewDetail}
    >
      <CardContent className="p-4 flex items-center gap-4">
        <Avatar className="h-10 w-10 rounded-xl flex-shrink-0">
          {!isAnonymous && c.avatar_url && <AvatarImage src={c.avatar_url} alt={displayName} />}
          <AvatarFallback className="rounded-xl bg-primary/10 text-primary text-sm font-bold uppercase">
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
           <div className="flex items-center gap-2 flex-wrap">
             <h3 className="font-display font-bold text-foreground truncate text-sm">{displayName}</h3>
              {isAnonymous && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 rounded-lg">
                  <EyeOff className="w-3 h-3 me-0.5" /> {t('hire.talent.anonymous')}
                </Badge>
              )}
              {c.visibility_mode === 'open' && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 shadow-[0_0_8px_hsl(145_60%_45%/0.35)] animate-pulse">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {t('hire.talent.openToWork')}
                </span>
              )}
            </div>
          {c.current_job_title && (
            <p className="text-xs text-primary font-medium mt-0.5 line-clamp-1">{c.current_job_title}</p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground mt-1">
            {c.country && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {c.city ? `${c.city}, ` : ''}{c.country}
              </span>
            )}
            {c.total_years_experience != null && (
              <span className="flex items-center gap-1">
                <Briefcase className="w-3 h-3" />
                {t('hire.talent.yearsShort', { count: c.total_years_experience })}
              </span>
            )}
          </div>
        </div>

        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); onViewDetail(); }}
          className="text-xs h-8 px-2 text-muted-foreground hover:text-foreground flex-shrink-0"
        >
          <Eye className="w-3.5 h-3.5 me-1" />
          {t('hire.talent.view')}
        </Button>

        {canInvite && (
          c.allow_invites === false ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="outline" disabled className="rounded-full text-xs h-8 px-3 opacity-60 pointer-events-none">
                    <Lock className="w-3 h-3 me-1" />
                      {t('hire.talent.closed')}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="z-[9999]"><p>{t('hire.talent.candidateNotAcceptingInvites')}</p></TooltipContent>
            </Tooltip>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onInvite(); }}
              className="rounded-full press-scale text-xs h-8 px-3 flex-shrink-0"
            >
              <Send className="w-3 h-3 me-1" />
                {t('hire.talent.invite')}
                {inviteCount > 0 && (
                <Badge variant="secondary" className="ms-1 text-[9px] px-1 py-0">{inviteCount}</Badge>
              )}
            </Button>
          )
        )}
      </CardContent>
    </Card>
  );
}

export default TalentDiscovery;
